import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { CheckCircle2, LockKeyhole, TriangleAlert } from 'lucide-react';
import anuKingdomLogo from '../assets/images/anu-kingdom-logo.webp';
import './Auth.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', {
        token,
        newPassword: formData.password,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page student-auth">
      <div className="auth-container">
        <Link to="/" className="auth-brand" aria-label="Anu Kingdom home">
          <img src={anuKingdomLogo} alt="Anu Kingdom" />
          <span>ANU KINGDOM</span>
          <small>Learning Platform</small>
        </Link>
          <div className="auth-header">
            <div className="role-icon alert-icon"><TriangleAlert size={25} aria-hidden="true" /></div>
            <h1>Invalid Link</h1>
            <p>This password reset link is invalid or has expired</p>
          </div>

          <div className="auth-links" style={{ marginTop: '30px' }}>
            <Link to="/forgot-password" className="btn-primary btn-student" style={{ textAlign: 'center', textDecoration: 'none', display: 'block', padding: '12px' }}>
              Request New Link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page student-auth">
      <div className="auth-container">
        <Link to="/" className="auth-brand" aria-label="Anu Kingdom home">
          <img src={anuKingdomLogo} alt="Anu Kingdom" />
          <span>ANU KINGDOM</span>
          <small>Learning Platform</small>
        </Link>
          <div className="auth-header">
            <div className="role-icon success-icon"><CheckCircle2 size={25} aria-hidden="true" /></div>
            <h1>Password Reset!</h1>
            <p>Your password has been successfully reset</p>
          </div>

          <div className="success-message">
            <p>You can now sign in with your new password.</p>
            <p>Redirecting to login page...</p>
          </div>

          <div className="auth-links" style={{ marginTop: '30px' }}>
            <Link to="/login" className="btn-primary btn-student" style={{ textAlign: 'center', textDecoration: 'none', display: 'block', padding: '12px' }}>
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page student-auth">
      <div className="auth-container">
        <Link to="/" className="auth-brand" aria-label="Anu Kingdom home">
          <img src={anuKingdomLogo} alt="Anu Kingdom" />
          <span>ANU KINGDOM</span>
          <small>Learning Platform</small>
        </Link>
        <div className="auth-header">
          <div className="role-icon student-icon"><LockKeyhole size={25} aria-hidden="true" /></div>
          <h1>Reset Password</h1>
          <p>Enter your new password</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter new password"
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm new password"
              required
            />
          </div>

          {error && <div className="error">{error}</div>}

          <button type="submit" className="btn-primary btn-student" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>

          <p className="auth-link">
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

export default ResetPassword;
